import {AlignType, HStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {Fragment} from 'react';

import {isNilOrEmpty} from 'ramda-adjunct';

import {Nullish, PartialWithNull, suffixTestId, TestIdProps} from 'shared/web';

import {getJustifyFromAlign} from '../../utils/getJustifyFromAlign';
import {ChipsProps} from '../Chip/Chips';
import {ButtonAction, ButtonActionProps} from './components/ButtonAction';
import {
  ButtonWithTooltipAction,
  ButtonWithTooltipActionProps,
} from './components/ButtonWithTooltipAction';
import {ChipsAction, ChipsActionProps} from './components/ChipsAction';
import {DropdownAction, DropdownActionProps} from './components/DropdownAction';
import {FormButtonAction, FormButtonActionProps} from './components/FormButtonAction';
import {IconButtonAction, IconButtonActionProps} from './components/IconButtonAction';
import {
  IconButtonWithTooltipAction,
  IconButtonWithTooltipActionProps,
} from './components/IconButtonWithTooltipAction';

interface CustomActionProps extends PartialWithNull<Omit<ChipsProps, 'type'>> {
  type: 'custom';
  component: React.ReactNode;
}

export type Action =
  | ButtonActionProps
  | DropdownActionProps
  | IconButtonActionProps
  | IconButtonWithTooltipActionProps
  | FormButtonActionProps
  | ButtonWithTooltipActionProps
  | ChipsActionProps
  | CustomActionProps;

export interface ActionsProps extends TestIdProps {
  actions: Action[] | Nullish;
  size?: 'small' | 'default';
  align?: AlignType;
}

export function Actions(props: ActionsProps) {
  if (isNilOrEmpty(props.actions)) {
    return null;
  }

  return (
    <HStack
      spacing={2}
      align="center"
      justify={getJustifyFromAlign(props.align)}
      wrap
      data-testid={props['data-testid']}
    >
      {props.actions?.map((action, index) => {
        const testId = action['data-testid'] ?? `[${index}]`;

        return match(action)
          .with({type: 'button'}, (currentAction) => (
            <ButtonAction
              // eslint-disable-next-line react/no-array-index-key -- parity: byte-identical to the legacy keying; index shifts from conditional callers match legacy remount semantics
              key={`buttonAction-[${currentAction.title}]-[${index}]`}
              size={props.size}
              data-testid={suffixTestId(testId, props)}
              {...currentAction}
            />
          ))
          .with({type: 'buttonWithTooltip'}, (currentAction) => (
            <ButtonWithTooltipAction
              // eslint-disable-next-line react/no-array-index-key -- parity: byte-identical to the legacy keying; index shifts from conditional callers match legacy remount semantics
              key={`buttonWithTooltipAction-[${currentAction.title}]-[${index}]`}
              size={props.size}
              data-testid={suffixTestId(testId, props)}
              {...currentAction}
            />
          ))
          .with({type: 'form-button'}, (currentAction) => (
            <FormButtonAction
              // eslint-disable-next-line react/no-array-index-key -- parity: byte-identical to the legacy keying; index shifts from conditional callers match legacy remount semantics
              key={`formButtonAction-[${currentAction.title}]-[${index}]`}
              size={props.size}
              data-testid={suffixTestId(testId, props)}
              {...currentAction}
            />
          ))
          .with({type: 'iconButton'}, (currentAction) => (
            <IconButtonAction
              // eslint-disable-next-line react/no-array-index-key -- parity: byte-identical to the legacy keying; index shifts from conditional callers match legacy remount semantics
              key={`iconButtonAction-[${index}]`}
              size={props.size}
              data-testid={suffixTestId(testId, props)}
              {...currentAction}
            />
          ))
          .with({type: 'iconButtonWithTooltip'}, (currentAction) => (
            <IconButtonWithTooltipAction
              // eslint-disable-next-line react/no-array-index-key -- parity: byte-identical to the legacy keying; index shifts from conditional callers match legacy remount semantics
              key={`iconButtonWithTooltipAction-[${index}]`}
              size={props.size}
              data-testid={suffixTestId(testId, props)}
              {...currentAction}
            />
          ))
          .with({type: 'chips'}, (currentAction) => (
            <ChipsAction
              // eslint-disable-next-line react/no-array-index-key -- parity: byte-identical to the legacy keying; index shifts from conditional callers match legacy remount semantics
              key={`chipsAction-[${index}]`}
              size={props.size}
              data-testid={suffixTestId(testId, props)}
              {...currentAction}
            />
          ))
          .with({type: 'dropdown-button'}, {type: 'dropdown-iconButton'}, (currentAction) => (
            <DropdownAction
              // eslint-disable-next-line react/no-array-index-key -- parity: byte-identical to the legacy keying; index shifts from conditional callers match legacy remount semantics
              key={`dropdownAction-[${index}]`}
              size={props.size}
              data-testid={suffixTestId(testId, props)}
              {...currentAction}
            />
          ))
          .with({type: 'custom'}, (currentAction) => (
            // eslint-disable-next-line react/no-array-index-key -- parity: byte-identical to the legacy keying; index shifts from conditional callers match legacy remount semantics
            <Fragment key={`customAction-[${index}]`}>{currentAction.component}</Fragment>
          ))
          .exhaustive();
      })}
    </HStack>
  );
}
